/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDBadge from "components/MDBadge";
import MDButton from "components/MDButton";
import { IconButton, Switch } from "@mui/material";

// Icons
import Icon from "@mui/material/Icon";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import EditIcon from "@mui/icons-material/Edit";

// Datos mock de empresas con información de inventario
const empresasInventarioMock = [
  {
    id: 1,
    nombre: "Innova Tech Solutions",
    email: "admin@innovatech.com",
    telefono: "+57 305 123 4567",
    nit: "900111222-3",
    sector: "Tecnología",
    tamaño: "Grande",
    tests_disponibles: 15,
    precio_test: 75000,
    estado: "ACTIVO",
    tipo_test: "Cuadrante H",
    fecha_ultima_actualizacion: "2024-01-15",
  },
  {
    id: 2,
    nombre: "Global Marketing Group",
    email: "contacto@gmg.com",
    telefono: "+57 306 234 5678", 
    nit: "900222333-4",
    sector: "Marketing",
    tamaño: "Mediana",
    tests_disponibles: 8,
    precio_test: 65000,
    estado: "ACTIVO",
    tipo_test: "Cuadrante H",
    fecha_ultima_actualizacion: "2024-01-10",
  },
  {
    id: 3,
    nombre: "Constructora Andina",
    email: "info@constructoraandina.com",
    telefono: "+57 307 345 6789",
    nit: "900333444-5",
    sector: "Construcción",
    tamaño: "Grande",
    tests_disponibles: 25,
    precio_test: 80000,
    estado: "ACTIVO",
    tipo_test: "Cuadrante H",
    fecha_ultima_actualizacion: "2024-01-20",
  },
  {
    id: 4,
    nombre: "Farmacias del Valle",
    email: "gerencia@farmavalle.com",
    telefono: "+57 308 456 7890",
    nit: "900444555-6",
    sector: "Salud",
    tamaño: "Mediana",
    tests_disponibles: 3,
    precio_test: 60000,
    estado: "INACTIVO",
    tipo_test: "Cuadrante H",
    fecha_ultima_actualizacion: "2024-01-05",
  },
  {
    id: 5,
    nombre: "Alimentos Frescos SAS",
    email: "ventas@alimentosfrescos.com",
    telefono: "+57 309 567 8901",
    nit: "900555666-7",
    sector: "Alimentos",
    tamaño: "Pequeña",
    tests_disponibles: 12,
    precio_test: 55000,
    estado: "ACTIVO",
    tipo_test: "Cuadrante H",
    fecha_ultima_actualizacion: "2024-01-18",
  },
  {
    id: 6,
    nombre: "Textiles Colombia",
    email: "produccion@textilescol.com",
    telefono: "+57 310 678 9012",
    nit: "900666777-8",
    sector: "Textil",
    tamaño: "Mediana",
    tests_disponibles: 0,
    precio_test: 70000,
    estado: "INACTIVO",
    tipo_test: "Cuadrante H",
    fecha_ultima_actualizacion: "2024-01-01",
  },
];

export default function testDisponiblesTableData(
  handleIncreaseTest,
  handleDecreaseTest,
  handleToggleStatus,
  handleEditTest,
  empresasData = empresasInventarioMock
) {
  const Company = ({ name, email }) => (
    <MDBox display="flex" alignItems="center" lineHeight={1}>
      <MDBox ml={2} lineHeight={1}>
        <MDTypography display="block" variant="button" fontWeight="medium">
          {name}
        </MDTypography>
        <MDTypography variant="caption">{email}</MDTypography>
      </MDBox>
    </MDBox>
  );

  // Typechecking props for the Company
  Company.propTypes = {
    name: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
  };

  const TestInfo = ({ tipo, cantidad }) => (
    <MDBox lineHeight={1} textAlign="left">
      <MDTypography display="block" variant="caption" color="text" fontWeight="medium">
        {tipo}
      </MDTypography>
      <MDTypography variant="caption">Cantidad: {cantidad}</MDTypography>
    </MDBox>
  );

  // Typechecking props for the TestInfo
  TestInfo.propTypes = {
    tipo: PropTypes.string.isRequired,
    cantidad: PropTypes.number.isRequired,
  };

  const QuantityControls = ({ empresa, cantidad }) => (
    <MDBox display="flex" alignItems="center" justifyContent="center" gap={1}>
      <IconButton
        onClick={() => handleDecreaseTest(empresa)}
        disabled={cantidad === 0}
        size="small"
        sx={{ 
          backgroundColor: "error.light", 
          color: "white", 
          "&:hover": { backgroundColor: "error.main" }, 
          width: 28, 
          height: 28 
        }}
      >
        <RemoveIcon fontSize="small" />
      </IconButton>
      <MDTypography 
        component="span" 
        sx={{ 
          mx: 1.5, 
          fontWeight: "medium", 
          fontSize: "1rem", 
          minWidth: "2ch", 
          textAlign: "center" 
        }}
      >
        {cantidad}
      </MDTypography>
      <IconButton
        onClick={() => handleIncreaseTest(empresa)}
        size="small"
        sx={{ 
          backgroundColor: "success.light", 
          color: "white", 
          "&:hover": { backgroundColor: "success.main" }, 
          width: 28, 
          height: 28 
        }}
      >
        <AddIcon fontSize="small" />
      </IconButton>
    </MDBox>
  );

  // Typechecking props for the QuantityControls
  QuantityControls.propTypes = {
    empresa: PropTypes.object.isRequired,
    cantidad: PropTypes.number.isRequired,
  };

  const StatusActions = ({ empresa, estado }) => (
    <MDBox display="flex" alignItems="center" justifyContent="center" gap={1}>
      <Switch
        checked={estado === "ACTIVO"}
        onChange={() => handleToggleStatus(empresa)}
        color="success"
        size="small"
      />
      <IconButton 
        color="primary" 
        onClick={() => handleEditTest(empresa)} 
        sx={{ color: "#FFC107" }}
      >
        <EditIcon />
      </IconButton>
    </MDBox>
  );

  // Typechecking props for the StatusActions
  StatusActions.propTypes = {
    empresa: PropTypes.object.isRequired,
    estado: PropTypes.string.isRequired,
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      ACTIVO: { color: "success", label: "Activo" },
      INACTIVO: { color: "error", label: "Inactivo" },
    };

    const config = statusConfig[status] || { color: "secondary", label: status };
    
    return (
      <MDBox ml={-1}>
        <MDBadge badgeContent={config.label} color={config.color} variant="gradient" size="sm" />
      </MDBox>
    );
  };

  // Usar los datos proporcionados por parámetro o los mock por defecto

  return {
    columns: [
      { Header: "empresa", accessor: "empresa", width: "25%", align: "left" },
      { Header: "test info", accessor: "testInfo", align: "left" },
      { Header: "precio", accessor: "precio", align: "center" },
      { Header: "cantidad", accessor: "cantidad", align: "center" },
      { Header: "estado", accessor: "estado", align: "center" },
      { Header: "acciones", accessor: "acciones", align: "center" },
    ],

    rows: empresasData.map((empresa) => ({
      empresa: <Company name={empresa.nombre} email={empresa.email} />,
      testInfo: <TestInfo tipo={empresa.tipo_test} cantidad={empresa.tests_disponibles} />,
      precio: (
        <MDTypography component="span" variant="caption" color="text" fontWeight="medium">
          ${empresa.precio_test.toLocaleString('es-CO')}
        </MDTypography>
      ),
      cantidad: <QuantityControls empresa={empresa} cantidad={empresa.tests_disponibles} />,
      estado: getStatusBadge(empresa.estado),
      acciones: <StatusActions empresa={empresa} estado={empresa.estado} />,
    })),
  };
}

export { testDisponiblesTableData, empresasInventarioMock }; 